import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const InactivityTimer = ({ children }) => {
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60); // 1 minuto de advertencia
  const countdownIntervalRef = useRef(null);

  // Configuración de tiempos (en minutos) - MODO PRUEBA
  const INACTIVITY_TIMEOUT = 15; // Cerrar sesión después de 2 min de inactividad
  const WARNING_TIME = 14; // Mostrar advertencia a 1 min (1 min antes)

  const logout = () => {
    
    // Limpiar todos los tokens y datos de sesión
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('bp_token');
    localStorage.removeItem('user_token');
    localStorage.removeItem('is_account');
    localStorage.removeItem('is_aggregator');
    localStorage.removeItem('selectedSucursal');
    
    // Limpiar timers
    clearAllTimers();
    
    // Redirigir a inicio
    navigate('/Inicio');
  };

  const clearAllTimers = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const startCountdown = () => {
    setRemainingSeconds(60); // Resetear a 1 minuto
    
    // Actualizar cada segundo
    countdownIntervalRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetTimer = () => {
    // Limpiar timers anteriores
    clearAllTimers();
    setShowWarning(false);

    // Timer de advertencia (1 minuto)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
    }, WARNING_TIME * 60 * 1000);

    // Timer de logout (2 minutos)
    inactivityTimerRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT * 60 * 1000);
  };

  const handleContinueSession = () => {
    setShowWarning(false);
    resetTimer();
  };

  useEffect(() => {
    // Lista de eventos que indican actividad INTENCIONAL del usuario
    // (No incluye mousemove ni scroll para evitar reinicios accidentales)
    const activityEvents = [
      'mousedown',    // Click del mouse
      'click',        // Click
      'keypress',     // Tecla presionada
      'keydown',      // Tecla presionada (alternativa)
      'touchstart'    // Touch en móviles
    ];

    // Throttle para evitar resetear el timer demasiado frecuentemente
    let isThrottled = false;
    const throttledResetTimer = () => {
      if (!isThrottled) {
        resetTimer();
        isThrottled = true;
        setTimeout(() => {
          isThrottled = false;
        }, 1000); // Resetear máximo una vez por segundo
      }
    };

    // Agregar listeners de eventos
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledResetTimer, { passive: true });
    });

    // Iniciar timer al montar el componente
    resetTimer();

    // Cleanup al desmontar
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledResetTimer);
      });
      clearAllTimers();
    };
  }, [navigate]);

  // Formatear tiempo restante (mm:ss)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}
      
      {/* Modal de advertencia de inactividad */}
      {showWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-in'
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '450px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            {/* Icono de advertencia */}
            <div style={{
              fontSize: '60px',
              marginBottom: '20px',
              color: '#FCAD2A'
            }}>
              ⚠️
            </div>

            {/* Título */}
            <h2 style={{
              color: '#3E3E3E',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '15px'
            }}>
              Sesión por expirar
            </h2>

            {/* Mensaje */}
            <p style={{
              color: '#666',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '16px',
              marginBottom: '10px',
              lineHeight: '1.5'
            }}>
              Tu sesión expirará por inactividad en:
            </p>

            {/* Contador */}
            <div style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: remainingSeconds <= 30 ? '#FF4444' : '#FCAD2A',
              fontFamily: 'monospace',
              marginBottom: '25px',
              transition: 'color 0.3s ease'
            }}>
              {formatTime(remainingSeconds)}
            </div>

            {/* Botón */}
            <button 
              onClick={handleContinueSession}
              style={{
                padding: '15px 40px',
                backgroundColor: '#0E9600',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                fontFamily: 'Montserrat, sans-serif',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(14, 150, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0C7D00';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(14, 150, 0, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#0E9600';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(14, 150, 0, 0.3)';
              }}
            >
              Continuar sesión
            </button>

            {/* Nota informativa */}
            <p style={{
              color: '#999',
              fontSize: '12px',
              marginTop: '20px',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              Por seguridad, cerramos sesión después de {INACTIVITY_TIMEOUT} minutos de inactividad
            </p>
          </div>
        </div>
      )}

      {/* Estilos de animación */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: translateY(-20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default InactivityTimer;